import { MapboxVectorLayer } from 'ol-mapbox-style';
import type BaseLayer from 'ol/layer/Base';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import TileDebug from 'ol/source/TileDebug';
import TileWMS from 'ol/source/TileWMS';
import XYZ from 'ol/source/XYZ';

import { isNonEmptyString } from '@nora-monorepo/shared';

import { MapSetup } from '#nora-ui/map/map/MapSetup';
import type { LayerConfig } from '#nora-ui/map/types/layer.types';

/**
 * Builds an OpenLayers base map layer from a NORA layer configuration.
 */
export function createBaseMapOlLayer(config: LayerConfig): BaseLayer {
  switch (config.layerType) {
    case 'GenericMapLayer':
      if (!isNonEmptyString(config.url)) {
        throw new Error(`Missing URL for GenericMapLayer with id ${config.id}. A valid URL is required to create this layer.`);
      }

      return new TileLayer({
        source: new XYZ({
          projection: 'EPSG:3857',
          url: decodeURI(config.url),
        }),
        zIndex: 0,
        visible: config.visible ?? true,
        properties: { id: config.id, name: config.name },
      });

    case 'MapBoxVectorLayer':
      if (!isNonEmptyString(config.styleUrl)) {
        throw new Error(`Missing styleUrl for MapBoxVectorLayer with id ${config.id}. A valid Mapbox style URL is required to create this layer.`);
      }

      return new MapboxVectorLayer({
        styleUrl: config.styleUrl,
        accessToken: config.accessToken,
        zIndex: 0,
        visible: config.visible ?? true,
        properties: { id: config.id, name: config.name },
      });

    case 'WmsMapLayer':
      return createWmsMapLayer(config);

    case 'TileGridLayer':
      return new TileLayer({
        source: new TileDebug(),
        visible: config.visible ?? true,
        properties: { id: config.id, name: config.name },
      });

    default:
      throw new Error(`Unknown layer type: ${config.layerType}`);
  }
}

function createWmsMapLayer(config: LayerConfig): TileLayer<TileWMS> {
  const wmsUrl = config.wmsUrl;

  if (!isNonEmptyString(wmsUrl)) {
    throw new Error(`Missing wmsUrl for WmsMapLayer with id ${config.id}. A valid WMS URL is required to create this layer.`);
  }

  if (!isNonEmptyString(config.layers)) {
    throw new Error(`Missing layers for WmsMapLayer with id ${config.id}. A valid WMS layer name is required to create this layer.`);
  }

  return new TileLayer({
    source: new TileWMS({
      url: wmsUrl,
      params: {
        LAYERS: config.layers,
        TILED: true,
      },
    }),
    zIndex: 0,
    visible: config.visible ?? true,
    properties: { id: config.id, name: config.name },
  });
}

/**
 * Builds the organisation's default base map layer for use in standalone mini maps.
 */
export function createDefaultBaseMapLayer(): BaseLayer {
  try {
    const mapSetup = MapSetup.getInstance();
    const baseMaps = mapSetup.getBaseMaps();
    const defaultBaseMapId = mapSetup.getDefaultBaseMapId(baseMaps);

    const config = isNonEmptyString(defaultBaseMapId)
      ? mapSetup.getConfigForBaseMapId(defaultBaseMapId)
      : undefined;

    if (config) {
      return createBaseMapOlLayer(config);
    }
  }
  catch (error) {
    console.warn('Falling back to OpenStreetMap base map; no configured base map available', error);
  }

  return new TileLayer({ source: new OSM() });
}
